import { useMemo, useSyncExternalStore } from 'react';

import { getPortalData, getPortalDataVersion, subscribePortalData } from 'services/portalData';

export interface TenantIdentity {
  /** Full display name, e.g. "Care& Family Health" */
  name: string;
  /** Short display name, e.g. "Care&" */
  nameShort: string;
  supportEmail?: string;
  website?: string;
}

const FALLBACK_NAME = 'your clinic';

/**
 * Tenant identity for user-facing copy, sourced from portal-data (available
 * pre- and post-auth — the app waits for the portal-data warmup before first
 * render). Neutral fallbacks; never hardcode a tenant brand in components.
 */
export function getTenantIdentity(): TenantIdentity {
  const data = getPortalData();
  return {
    name: data?.name || FALLBACK_NAME,
    nameShort: data?.name_short || data?.name || FALLBACK_NAME,
    supportEmail: data?.support_email || undefined,
    website: data?.company_website || undefined,
  };
}

export function useTenantIdentity(): TenantIdentity {
  // portal-data is rewritten by the post-login re-warm and wiped by a logout
  // that clears transient branding, so mounted consumers have to follow it.
  const version = useSyncExternalStore(subscribePortalData, getPortalDataVersion);
  // memoized so the identity is safe in a consumer's dependency array
  return useMemo(() => getTenantIdentity(), [version]);
}

export default useTenantIdentity;
